import React, { useRef, useEffect, useState, useCallback } from 'react';
import remarkGfm from 'remark-gfm';
import InfiniteScroll from 'react-infinite-scroll-component';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '../../../../../../../../app/hooks';
import { humanizeTokens } from '../../../../../../../../utils/utils';

import { errorIconRed, POKER_CHIP_ICON } from '../../../../../../../../assets/hostedassets';
import { Button } from '../../../../../../../../common/_custom';
import MagicUploadIcon from '../../../../../../../../assets/Svg/MagicuploadIcon';
import CopyIcon from '../../../../../../../../assets/Svg/CopyIcon';
import TickIcon from '../../../../../../../../assets/Svg/TickIcon';
import Progressbar from '../../../../../../../../common/Progressbar/Progressbar';
import useCopyText from '../../../../../../../../common/_custom/Hooks/useCopyText/useCopyText';
import { MessageType } from '../../../../../../../../store/dashboard/askAica/constants';
import { Message } from '../../../../../../../../store/dashboard/askAica/actions';
import { downloadAskAicaFile } from '../../../../../../../../store/dashboard/askAica/apiHelper';
import { AskAicaFileRef } from '../../hooks/useAskAicaFileActions';

import FileAttachmentCard from '../FileAttachmentCard/FileAttachmentCard';
import CitationAnswer from './CitationAnswer';
import FeedbackAckBubble from './FeedbackAckBubble';

import styles from './MessagesContainer.module.scss';

const formatSkillName = (skill: string) =>
    skill
        .split(/[-_]/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

/**
 * Whether the cost-weighted token-usage pill should render for a message:
 * an assistant reply that isn't an error, isn't the still-streaming placeholder,
 * and carries a positive, finite agg_tokens value.
 */
const shouldShowTokenUsage = (message: Message, isError: boolean, isStreamingPlaceholder: boolean): boolean => {
    const aggTokens = message.metadata?.agg_tokens;
    return (
        message.type === MessageType.ASSISTANT &&
        !isError &&
        !isStreamingPlaceholder &&
        typeof aggTokens === 'number' &&
        Number.isFinite(aggTokens) &&
        aggTokens > 0
    );
};

const SkillChips: React.FC<{ skills?: string[] }> = ({ skills }) => {
    if (!skills?.length) return null;
    return (
        <div className={styles.SkillChips} data-testid="skill-chips">
            {skills.map((skill) => (
                <span key={skill} className={styles.SkillChip}>
                    ✦ {formatSkillName(skill)}
                </span>
            ))}
        </div>
    );
};

interface MessagesContainerProps {
    isLoadingChatHistory: boolean;
    hasMoreData: boolean;
    messagesEndRef: React.RefObject<HTMLDivElement>;
    fetchChatHistory: Function;
    streamStarted?: boolean;
    retryMessage: Function;
    scrollToBottom?: boolean;
    initialFetchRef: React.MutableRefObject<boolean>;
    containerRef?: React.RefObject<HTMLDivElement>;
    /** Opens a generated file in the matching in-app viewer (docx/xlsx/pdf). */
    onOpenFile: (file: AskAicaFileRef) => void;
    /** Snapshots a generated file into Reports (docx/xlsx/txt). Absent when the reports.addToReports visibility key is off. */
    onAddFileToReports?: (file: AskAicaFileRef) => void;
}

const MessagesContainer: React.FC<MessagesContainerProps> = ({
    messagesEndRef,
    isLoadingChatHistory,
    hasMoreData,
    fetchChatHistory,
    streamStarted,
    retryMessage,
    scrollToBottom,
    initialFetchRef,
    containerRef,
    onOpenFile,
    onAddFileToReports,
}) => {
    const internalContainerRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = containerRef || internalContainerRef;
    const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const reasoningScrollableDivRef = useRef<HTMLDivElement>(null);
    const contentRefsMap = useRef<Map<number, HTMLElement>>(new Map());

    const { messages, reasoning, isLoading } = useAppSelector((state) => state.dashboard.askAicaReducer);
    const { copiedId, copyDirectText, copyElement } = useCopyText();

    const handleCopyMessage = useCallback(
        (messageId: number, messageType: string, messageContent: string) => {
            if (messageType === MessageType.USER) {
                copyDirectText(messageContent, messageId);
                return;
            }
            const el = contentRefsMap.current.get(messageId);
            if (el) copyElement(el, messageId);
        },
        [copyDirectText, copyElement],
    );

    // While streaming, messages[0] is the assistant placeholder being filled —
    // its metadata accumulates skills_invoked chunks emitted by Athena.
    const streamingSkills = isLoading && messages[0]?.type === MessageType.ASSISTANT ? messages[0]?.metadata?.skills_invoked : undefined;

    // Download must run under the same context org the chat used (the portfolio
    // org from the route), since Athena owns and gates the file by that org.
    const { id: contextOrgId } = useParams();

    const handleDownloadFile = useCallback(
        async (fileUrl: string, fileName: string) => {
            try {
                await downloadAskAicaFile(fileUrl, fileName, contextOrgId);
            } catch (err) {
                console.error('File download failed:', err);
                toast.error('Failed to download the file. Please try again.');
            }
        },
        [contextOrgId],
    );

    const [previousScrollHeight, setPreviousScrollHeight] = useState(0);
    const [shouldMaintainScrollPosition, setShouldMaintainScrollPosition] = useState(false);
    const [progressPercent, setProgressPercent] = useState(0);

    // Clean up stale content refs when messages change
    useEffect(() => {
        const currentIds = new Set(messages.map((m) => m.id));
        contentRefsMap.current.forEach((_, id) => {
            if (!currentIds.has(id)) contentRefsMap.current.delete(id);
        });
    }, [messages]);

    useEffect(() => {
        if (shouldMaintainScrollPosition && messagesContainerRef.current) {
            const container = messagesContainerRef.current;
            const newScrollHeight = container.scrollHeight;
            const scrollDifference = newScrollHeight - previousScrollHeight;

            // Adjust scroll position to maintain view
            container.scrollTop = container.scrollTop + scrollDifference;

            // Reset flags
            setShouldMaintainScrollPosition(false);
            setPreviousScrollHeight(0);
        }
    }, [messages, shouldMaintainScrollPosition, previousScrollHeight]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (messages.length && initialFetchRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            initialFetchRef.current = false;
        }
    }, [messages, isLoading]);

    // Scroll to bottom when scrollToBottom prop is true
    useEffect(() => {
        if (scrollToBottom && messagesContainerRef.current) {
            messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [scrollToBottom]);

    useEffect(() => {
        // Start progress animation when reasoning first appears and we're loading
        if (reasoning?.message && isLoading && !progressIntervalRef.current) {
            setProgressPercent(0);
            const duration = 30000; // 30 seconds
            const targetPercent = 95;
            const intervalTime = 50; // Update every 50ms for smooth animation
            const increment = (targetPercent / duration) * intervalTime;

            const interval = setInterval(() => {
                setProgressPercent((prev) => {
                    const newPercent = prev + increment;
                    if (newPercent >= targetPercent) {
                        return targetPercent;
                    }
                    return newPercent;
                });
            }, intervalTime);

            progressIntervalRef.current = interval;
        }

        // Reset progress when loading is complete
        if (!isLoading && progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
            // Complete to 100% before resetting
            setProgressPercent(100);
            setTimeout(() => setProgressPercent(0), 500); // Hold at 100% briefly before hiding
        }
    }, [reasoning, isLoading]);

    // Cleanup interval on component unmount
    useEffect(() => {
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (reasoningScrollableDivRef.current) {
            reasoningScrollableDivRef.current.scrollTop = reasoningScrollableDivRef.current.scrollHeight;
        }
    }, [reasoning?.message]);

    return (
        <div className={styles.MessagesContainer} id="scrollableDiv" ref={messagesContainerRef}>
            <div ref={messagesEndRef} />
            <InfiniteScroll
                dataLength={messages.length}
                hasMore={hasMoreData}
                next={() => {
                    if (!isLoadingChatHistory && !isLoading && hasMoreData) {
                        messagesContainerRef.current && setPreviousScrollHeight(messagesContainerRef.current.scrollHeight);
                        setShouldMaintainScrollPosition(true);
                        fetchChatHistory(true);
                    }
                }}
                style={{ display: 'flex', flexDirection: 'column-reverse' }}
                loader={false}
                inverse={true}
                scrollableTarget="scrollableDiv"
            >
                {isLoading && !streamStarted && (
                    <div className={styles.ThinkingMessage}>
                        <div className={`${styles.Message} ${styles.AiMessage}`}>
                            <div className={styles.MessageContent}>
                                <div className={styles.MessageBubble}>
                                    <div className={styles.TypingIndicator}>
                                        <div className={styles.IconShimmerContainer}>
                                            <MagicUploadIcon colour="#545B62" height="20" width="20" />
                                        </div>
                                        <span className={styles.ThinkingText} style={{ marginLeft: '16px' }}>
                                            Thinking
                                        </span>
                                        <span className={styles.Ellipsis}></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {reasoning?.message && (
                            <div className={styles.ReasoningContainer} ref={reasoningScrollableDivRef}>
                                <SkillChips skills={streamingSkills} />
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{reasoning.message}</ReactMarkdown>
                                <Progressbar width={progressPercent} height={6} color="#000000" />
                            </div>
                        )}
                    </div>
                )}
                {messages.map((message, index) => {
                    // Check if this is an error message
                    const isError = message.error || message.metadata?.error || message.status === 'error';
                    const errorMessage = message.errorMessage || message.metadata?.errorMessage;
                    // Newest assistant message while its response is still streaming in.
                    const isStreamingPlaceholder = isLoading && index === 0 && message.type === MessageType.ASSISTANT;
                    // Captured as a local (rather than re-reading message.metadata?.file_url inside the
                    // onCardClick/onAddToReports closures below) so TS narrows it to non-optional there.
                    const fileAttachment: AskAicaFileRef | null = message.metadata?.file_url
                        ? {
                              url: message.metadata.file_url,
                              name: message.metadata.file_name || 'document',
                              type: message.metadata.file_type,
                          }
                        : null;

                    return (
                        <div
                            key={message.id}
                            data-message-type={message.type}
                            className={`${styles.Message} ${message.type === MessageType.USER ? styles.UserMessage : styles.AiMessage} ${
                                isError ? styles.ErrorMessage : ''
                            }`}
                        >
                            <div className={styles.MessageContent}>
                                {(message.type === MessageType.USER || message.content || streamStarted || isError) && (
                                    <>
                                        <div className={`${styles.MessageBubble} ${isError ? styles.ErrorBubble : ''}`}>
                                            {message.type === MessageType.ASSISTANT ? (
                                                // Feedback ack — isFeedbackAck is the canonical flag (promoted by the reducer
                                                // from metadata.feedback_ack on live turns, set by history map on reload).
                                                // metadata.feedback_ack is kept as a fallback for test scenarios where the
                                                // message is constructed without passing through the reducer (A-14, A-17).
                                                (message.isFeedbackAck || message.metadata?.feedback_ack) ? (
                                                    <FeedbackAckBubble
                                                        content={message.content}
                                                        registerContentRef={(el) => contentRefsMap.current.set(message.id, el)}
                                                    />
                                                ) : isError ? (
                                                    <p className={styles.ErrorText}>
                                                        <div className={styles.Top}>
                                                            <img src={errorIconRed} alt="err" height={'20px'} />
                                                            {errorMessage || message.content}
                                                        </div>
                                                    </p>
                                                ) : (
                                                    <div className={styles.AssistantMessageContainer}>
                                                        <div className={styles.MagicUploadIcon}>
                                                            <MagicUploadIcon colour="#545B62" height="20" width="20" />
                                                        </div>
                                                        <div className={styles.AssistantMessageContent}>
                                                            <SkillChips skills={message.metadata?.skills_invoked} />
                                                            {/* Copy target excludes the skill chips above */}
                                                            <CitationAnswer
                                                                message={message}
                                                                registerContentRef={(el) => {
                                                                    if (el) contentRefsMap.current.set(message.id, el);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )
                                            ) : (
                                                // Feedback user messages render like any normal user message.
                                                <p>{message.content}</p>
                                            )}
                                        </div>
                                        {fileAttachment && (
                                            <FileAttachmentCard
                                                fileUrl={fileAttachment.url}
                                                fileName={fileAttachment.name}
                                                fileType={fileAttachment.type}
                                                onDownload={handleDownloadFile}
                                                onCardClick={() => onOpenFile(fileAttachment)}
                                                onAddToReports={onAddFileToReports ? () => onAddFileToReports(fileAttachment) : undefined}
                                            />
                                        )}
                                        <div className={styles.MessageMeta}>
                                            {shouldShowTokenUsage(message, isError, isStreamingPlaceholder) && (
                                                <div className={styles.TokenGroup}>
                                                    <img src={POKER_CHIP_ICON} width={14} height={14} alt="" />
                                                    <span className={styles.TokenText}>
                                                        {humanizeTokens(message.metadata?.agg_tokens ?? 0)} tokens
                                                    </span>
                                                </div>
                                            )}
                                            <span className={styles.Timestamp}>
                                                {new Date(message.timestamp)
                                                    .toLocaleString('en-US', {
                                                        hour: 'numeric',
                                                        minute: '2-digit',
                                                        hour12: true,
                                                    })
                                                    .replace(',', '')}{' '}
                                                {new Date(message.timestamp)
                                                    .toLocaleString('en-US', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric',
                                                    })
                                                    .replace(',', '')}
                                            </span>
                                            {!isError && !isStreamingPlaceholder && (
                                                <button
                                                    className={styles.CopyButton}
                                                    onClick={() => handleCopyMessage(message.id, message.type, message.content)}
                                                    title="Copy to clipboard"
                                                >
                                                    {copiedId === message.id ? (
                                                        <TickIcon width={14} height={14} color="#49A71B" />
                                                    ) : (
                                                        <CopyIcon width="14" height="14" colour="#6b7280" />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                        {isError && index === 0 && (
                                            <Button
                                                text="Try Again"
                                                onClick={() => retryMessage(index)}
                                                style={{ marginTop: '0', marginLeft: 'auto', padding: '10px 16px' }}
                                                variant="purpleinverted"
                                                fit
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </InfiniteScroll>
        </div>
    );
};

export default MessagesContainer;
